import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { GolfActivityDto, GolfImportResultDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, PageHeader, SelectMenu, Spinner } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { ApiError, apiGet, apiPost } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

interface Picked {
  filename: string;
  content: string;
}

export function GolfImportPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const fileRef = useRef<HTMLInputElement>(null);

  const [types, setTypes] = useState<string[] | null>(null);
  const [activityType, setActivityType] = useState('');
  const [file, setFile] = useState<Picked | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<GolfActivityDto | null>(null);

  useEffect(() => {
    apiGet<{ types: string[] }>('/api/health/golf/activity-types')
      .then((r) => {
        setTypes(r.types);
        setActivityType(r.types[0] ?? '');
      })
      .catch(() => setErr('Failed to load activity types.'));
  }, []);

  const onPick = (f: File | undefined) => {
    setErr(null);
    setDone(null);
    if (!f) {
      setFile(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFile({ filename: f.name, content: String(reader.result ?? '') });
    reader.onerror = () => setErr('Could not read that file.');
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!file || !activityType) return;
    setBusy(true);
    setErr(null);
    setDone(null);
    try {
      const res = await apiPost<GolfImportResultDto>('/api/health/golf/import', {
        filename: file.filename,
        content: file.content,
        activityType,
      });
      setDone(res.activity);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      if (e instanceof ApiError) {
        const msg = (e.body as { message?: string } | null)?.message;
        setErr(
          e.status === 409
            ? (msg ?? 'This file has already been imported.')
            : (msg ?? 'Import failed. Please check the file and try again.'),
        );
      } else {
        setErr('Import failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Import</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Golf' }, { label: 'Import' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}
        {done && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            Imported <strong>{done.type}</strong> on {done.date} — {done.shotCount} shots, {done.clubCount} clubs.{' '}
            <a
              href="/health/golf/activity"
              onClick={(e) => {
                e.preventDefault();
                navigate('/health/golf/activity');
              }}
            >
              View activities
            </a>
            .
          </Alert>
        )}

        <div className="card" style={{ maxWidth: 640 }}>
          <div className="card-header">
            <h5 className="mb-0">Import a session CSV</h5>
          </div>
          <div className="card-body">
            <p className="text-muted">
              Upload a launch-monitor CSV export. Each file becomes one activity; importing the same
              file twice is prevented automatically.
            </p>

            {!types ? (
              <Spinner size="sm" />
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label">Activity type</label>
                  <SelectMenu
                    className="mb-0"
                    icon="ph-flag"
                    value={activityType}
                    onChange={setActivityType}
                    options={types.map((t) => ({ value: t, label: t }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={(e) => onPick(e.target.files?.[0])}
                  />
                  {file && <div className="form-text">Selected: {file.filename}</div>}
                </div>

                <Button
                  variant="primary"
                  icon="ph-upload-simple"
                  disabled={!file || !activityType || busy}
                  onClick={() => void submit()}
                >
                  {busy ? 'Importing…' : 'Import'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </AppChrome>
  );
}
